import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import Button from '../../../../../../../ui/canon/Button';
import SingleColumnTemplate from '../../../../../../../ui/layouts/SingleColumnTemplate';

const SuccessionCfaViewExamples = ({ cashFlows, currentUser, intl }) => {
  // Ensure we have cash flows to display
  if (!cashFlows || cashFlows.length === 0) {
    return (
      <SingleColumnTemplate>
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[var(--succession-text-primary)] mb-4">
            {intl.formatMessage({ id: 'succession.cash_flow_analysis.view_examples.no_cash_flows_heading' })}
          </h2>
          <p className="text-[var(--succession-text-secondary)]">
            {intl.formatMessage({ id: 'succession.cash_flow_analysis.view_examples.no_cash_flows_body' })}
          </p>
        </div>
      </SingleColumnTemplate>
    );
  }

  // Define the analysis types with their characteristics
  const analysisTypes = [
    {
      id: 'lump_sum',
      title: intl.formatMessage({ id: 'succession.cash_flow_analysis.view_examples.analysis_type_conservative_title' }),
      subtitle: intl.formatMessage({ id: 'succession.cash_flow_analysis.view_examples.analysis_type_conservative_subtitle' }),
      description: intl.formatMessage({ id: 'succession.cash_flow_analysis.view_examples.analysis_type_conservative_description' }),
      features: ['Full ownership transfer', 'Immediate payment', 'Financing assistance'],
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      gradient: 'from-succession-primary to-succession-primary-hover',
      borderColor: 'border-[var(--succession-border)]',
      bgColor: 'bg-[var(--succession-bg-tertiary)]'
    },
    {
      id: 'fixed_earnout',
      title: intl.formatMessage({ id: 'succession.cash_flow_analysis.view_examples.analysis_type_balanced_title' }),
      subtitle: intl.formatMessage({ id: 'succession.cash_flow_analysis.view_examples.analysis_type_balanced_subtitle' }),
      description: intl.formatMessage({ id: 'succession.cash_flow_analysis.view_examples.analysis_type_balanced_description' }),
      features: ['Predictable payments', 'Risk mitigation', 'Gradual transition'],
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 00-2-2z" />
        </svg>
      ),
      gradient: 'from-succession-primary to-succession-primary-hover',
      borderColor: 'border-[var(--succession-border)]',
      bgColor: 'bg-[var(--succession-bg-tertiary)]'
    },
    {
      id: 'contingent_earnout',
      title: intl.formatMessage({ id: 'succession.cash_flow_analysis.view_examples.analysis_type_aggressive_title' }),
      subtitle: intl.formatMessage({ id: 'succession.cash_flow_analysis.view_examples.analysis_type_aggressive_subtitle' }),
      description: intl.formatMessage({ id: 'succession.cash_flow_analysis.view_examples.analysis_type_aggressive_description' }),
      features: ['Performance incentives', 'Shared risk/reward', 'Growth alignment'],
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      gradient: 'from-succession-primary to-succession-primary-hover',
      borderColor: 'border-[var(--succession-border)]',
      bgColor: 'bg-[var(--succession-bg-tertiary)]'
    }
  ];

  return (
    <SingleColumnTemplate variant="wide">
      <div className="text-center mb-12">
        {/* Enhanced Header */}
        <div className="mb-8">
          <h2 className="text-4xl font-bold text-[var(--succession-text-primary)] mb-4">
            <FormattedMessage id="calculators.cfa_wizard.congratulations_below_are_your_3"/>
          </h2>
          <p className="text-lg text-[var(--succession-text-secondary)] max-w-3xl mx-auto">
            {intl.formatMessage({ id: 'succession.cash_flow_analysis.view_examples.congratulations_body' })}
          </p>
        </div>
        
        {/* Enhanced Cash Flow Examples Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          {analysisTypes.map((type, index) => {
            const cashFlow = cashFlows[index];
            if (!cashFlow) return null;
            
            return (
              <div key={type.id} className="group">
                <a href={`/succession/calculators/cfa/${cashFlow.id}`} style={{ color: 'var(--succession-text-primary)' }} className="block no-underline">
                  <div className={`relative bg-[var(--succession-bg-primary)] rounded-2xl shadow-succession-lg border-2 ${type.borderColor} overflow-hidden hover:shadow-succession-xl transform hover:scale-105 transition-all duration-300 h-full`}>
                    {/* Header */}
                    <div className="p-6 border-b border-[var(--succession-border)]">
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-[var(--succession-text-primary)]">
                          {type.icon}
                        </div>
                        <div className="bg-[var(--succession-bg-tertiary)] border border-[var(--succession-border)] px-3 py-1 rounded-full">
                          <span className="text-sm font-semibold text-[var(--succession-text-primary)]">{intl.formatMessage({ id: 'succession.cash_flow_analysis.view_examples.scenario_label' }, { number: index + 1 })}</span>
                        </div>
                      </div>
                      <h3 className="text-xl font-bold mb-2 text-[var(--succession-text-primary)]">
                        {type.title}
                      </h3>
                      <p className="text-[var(--succession-text-secondary)] text-sm">
                        {type.subtitle}
                      </p>
                    </div>

                    {/* Content Area */}
                    <div className="p-6">
                      <p className="text-[var(--succession-text-primary)] mb-6 leading-relaxed">
                        {type.description}
                      </p>

                      {/* Features List */}
                      <div className="space-y-3 mb-6">
                        {type.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center">
                            <div className="w-2 h-2 rounded-full bg-succession-primary mr-3"></div>
                            <span className="text-sm text-[var(--succession-text-primary)]">{feature}</span>
                          </div>
                        ))}
                      </div>

                      {/* Action Button */}
                      <div className={`${type.bgColor} border ${type.borderColor} rounded-xl p-4 group-hover:shadow-succession-md transition-all duration-200`}>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-[var(--succession-text-primary)]">{intl.formatMessage({ id: 'succession.cash_flow_analysis.view_examples.view_details' })}</span>
                          <svg className="w-5 h-5 text-[var(--succession-text-primary)] group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                        <p className="text-xs text-[var(--succession-text-primary)] mt-1">
                          {intl.formatMessage({ id: 'succession.cash_flow_analysis.view_examples.explore_description' })}
                        </p>
                      </div>
                    </div>
                  </div>
                </a>
              </div>
            );
          })}
        </div>

        {/* Enhanced Navigation Buttons */}
        <div className="bg-gradient-to-r from-[var(--succession-bg-secondary)] to-[var(--succession-bg-tertiary)] rounded-2xl p-8">
          <div className="text-center mb-6">
            <h3 className="text-xl font-semibold text-[var(--succession-text-primary)] mb-2">{intl.formatMessage({ id: 'succession.cash_flow_analysis.view_examples.ready_to_explore' })}</h3>
            <p className="text-[var(--succession-text-secondary)]">{intl.formatMessage({ id: 'succession.cash_flow_analysis.view_examples.explore_more_body' })}</p>
          </div>
          
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              as="a"
              href="/succession/calculators/cfa"
              variant="outline"
              size="lg"
              className="px-8 py-4"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              {intl.formatMessage({ id: 'succession.cash_flow_analysis.view_examples.view_all_cash_flows' })}
            </Button>
            <Button
              as="a"
              href="/succession/calculators/cfa/start"
              variant="primary"
              size="lg"
              className="px-8 py-4"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {intl.formatMessage({ id: 'succession.cash_flow_analysis.view_examples.create_new_analysis' })}
            </Button>
          </div>
        </div>
      </div>
    </SingleColumnTemplate>
  );
};

export default injectIntl(SuccessionCfaViewExamples); 